import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TimeLeft } from "./TimeLeft";
import {
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  HeartHandshake,
  Users,
  Check,
  ArrowRight,
  Star,
  Sparkles,
  Briefcase,
  Building2,
  GraduationCap,
  Rocket,
  Wrench,
  Coffee,
  Truck,
  Wallet,
  TrendingUp,
  Heart,
  Globe2,
  Cpu,
  Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";

const ICONS = {
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  HeartHandshake,
  Users,
  Sparkles,
  Briefcase,
  Building2,
  GraduationCap,
  Rocket,
  Wrench,
  Coffee,
  Truck,
  Wallet,
  TrendingUp,
  Heart,
  Globe2,
  Cpu,
  Eye,
};

export const ProductGrid = () => {
  const { currency } = useCurrency();
  const [products, setProducts] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products?currency=${currency}`)
      .then((res) => {
        const all = res.data.products || [];
        setProducts(all.filter((p) => !p.is_bundle));
        setBundle(all.find((p) => p.is_bundle));
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [currency]);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="bg-white py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="font-accent text-[#C5A059] text-xs">CHOOSE YOUR LAUNCH PATH</span>
          <h2
            data-testid="products-heading"
            className="font-display text-4xl sm:text-5xl text-[#0A192F] mt-4 mb-5"
          >
            Five Industries.
            <span className="italic"> One Decision.</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6">
            Each package is a complete investor-grade blueprint &mdash; not a template. Pick the
            opportunity that fits you and start in days, not months.
          </p>
          <a
            href="#bundle"
            data-testid="bundle-teaser-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-[#C5A059] hover:text-[#0A192F] border border-[#C5A059]/40 hover:border-[#0A192F] px-4 py-2 transition"
          >
            <Star className="w-3.5 h-3.5 fill-[#C5A059] text-[#C5A059]" />
            CAN'T DECIDE? GET ALL 5 FOR $99 (SAVE 43%)
          </a>
        </div>

        {loading ? (
          <div data-testid="products-loading" className="text-center text-slate-400 py-12">
            Loading plans...
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((p, idx) => {
              const Icon = ICONS[p.icon] || Sparkles;
              // NEW RELEASE window: 0-14 days after `created_at`.
              // Days 12-13 fade the badge (100 → 85 → 60 → gone) so the
              // "new" status trails off gracefully instead of vanishing abruptly.
              const ageDays = p.created_at
                ? (Date.now() - new Date(p.created_at).getTime()) / 86400000
                : Infinity;
              const isNewRelease = ageDays < 14;
              const newReleaseOpacity =
                ageDays < 12 ? "opacity-100"
                : ageDays < 13 ? "opacity-85"
                : "opacity-60";
              return (
                <article
                  key={p.id}
                  data-testid={`product-card-${p.slug}`}
                  className="group relative bg-white border border-slate-200 hover:border-[#C5A059] transition shadow-sm hover:shadow-2xl hover:shadow-slate-200/60 flex flex-col overflow-hidden"
                >
                  {p.bestseller && (
                    <div
                      data-testid={`bestseller-${p.slug}`}
                      className="absolute top-4 left-4 z-10 bg-[#C5A059] text-[#0A192F] font-accent text-[10px] px-3 py-1"
                    >
                      <Star className="inline w-3 h-3 mr-1 -mt-0.5" />
                      BEST SELLER
                    </div>
                  )}

                  {isNewRelease && (
                    <div
                      data-testid="new-release-badge"
                      className={`absolute left-4 z-10 inline-flex items-center gap-1 bg-[#D4AF37] text-[#0A192F] font-accent text-[10px] tracking-widest px-2.5 py-1 shadow-[0_2px_8px_-2px_rgba(212,175,55,0.55)] ring-1 ring-[#0A192F]/10 transition-opacity duration-500 ${newReleaseOpacity} ${
                        p.bestseller ? "top-14" : "top-4"
                      }`}
                    >
                      <Sparkles className="w-3 h-3 -mt-px" strokeWidth={2.5} />
                      NEW RELEASE
                    </div>
                  )}

                  {p.pre_order && (
                    <div
                      data-testid={`preorder-${p.slug}`}
                      className="absolute top-4 right-4 z-10 bg-[#0A192F] text-[#C5A059] font-accent text-[10px] px-3 py-1 border border-[#C5A059]/60"
                    >
                      PRE-ORDER
                    </div>
                  )}

                  {p.on_sale && !p.pre_order && (
                    <div
                      data-testid={`sale-badge-${p.slug}`}
                      className="absolute top-4 right-4 z-10 bg-[#0A192F] text-[#D4AF37] font-accent text-[10px] px-3 py-1 border border-[#D4AF37] shadow-[0_0_0_1px_#D4AF37]"
                    >
                      {p.sale_label || "SALE"}
                    </div>
                  )}

                  {p.image_url ? (
                    <div className="relative w-full bg-[#0A192F] overflow-hidden" style={{ aspectRatio: "2 / 3" }}>
                      <img
                        src={p.image_url}
                        alt={p.name}
                        loading="lazy"
                        data-testid={`cover-${p.slug}`}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                    </div>
                  ) : null}

                  <div className="p-8 flex flex-col flex-1">
                    {!p.image_url && (
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center">
                          <Icon className="w-6 h-6 text-[#C5A059]" />
                        </div>
                        <span className="font-accent text-[10px] text-slate-400">{p.category}</span>
                      </div>
                    )}

                    {p.image_url && (
                      <div className="flex items-center justify-between mb-3 -mt-1">
                        <span className="font-accent text-[10px] text-slate-400 tracking-wider">{p.category}</span>
                        {p.author && (
                          <span className="font-accent text-[10px] text-[#C5A059] tracking-wider">
                            BY {p.author.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )}

                    <h3 className="font-display text-2xl text-[#0A192F] leading-tight mb-2">
                      {p.name}
                    </h3>
                    <p className="text-[#C5A059] text-sm italic mb-5">{p.tagline}</p>

                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                      {p.short_description}
                    </p>

                    {p.pre_order && p.launch_date && (
                      <div
                        data-testid={`launch-date-${p.slug}`}
                        className="mb-5 bg-[#FBF8F1] border-l-4 border-[#C5A059] px-4 py-3 text-xs text-[#0A192F]"
                      >
                        <span className="font-accent tracking-wider text-[10px] text-[#C5A059]">LAUNCH DAY</span>
                        <div className="font-bold mt-0.5">{p.launch_date}</div>
                      </div>
                    )}

                    <ul className="space-y-2 mb-8">
                      {p.highlights.slice(0, 4).map((h) => (
                        <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-6 border-t border-slate-200">
                      {p.on_sale && p.sale_expires_at && (
                        <div className="-mx-8 mb-4">
                          <TimeLeft
                            expiresAt={p.sale_expires_at}
                            label={p.sale_label ? `${p.sale_label} ENDS IN` : "ELITE SALE ENDS IN"}
                          />
                        </div>
                      )}
                      <div className="flex items-baseline gap-3 mb-5">
                        <span
                          data-testid={`price-${p.slug}`}
                          className={`font-display text-3xl ${p.on_sale ? "text-[#D4AF37]" : "text-[#0A192F]"}`}
                        >
                          {p.symbol}
                          {p.price.toFixed(2)}
                        </span>
                        <span className="text-slate-400 line-through text-base">
                          {p.symbol}
                          {p.original_price.toFixed(2)}
                        </span>
                        <span className={`font-accent text-[10px] ml-auto ${p.on_sale ? "text-[#D4AF37]" : "text-[#C5A059]"}`}>
                          {Math.round(
                            ((p.original_price - p.price) / p.original_price) * 100
                          )}
                          % OFF
                        </span>
                      </div>
                      <Link
                        to={`/checkout/${p.id}`}
                        data-testid={`buy-button-${p.slug}`}
                        className="w-full bg-[#0A192F] hover:bg-[#C5A059] text-white hover:text-[#0A192F] font-bold py-3.5 px-5 text-sm inline-flex items-center justify-center gap-2 transition"
                      >
                        {p.pre_order ? "RESERVE MY COPY" : "GET INSTANT ACCESS"}
                        <ArrowRight className="w-4 h-4" />
                      </Link>

                      {bundle && !p.pre_order && (
                        <Link
                          to={`/checkout/${bundle.id}`}
                          data-testid={`upsell-bundle-${p.slug}`}
                          className="group mt-3 flex items-center justify-between gap-3 px-4 py-2.5 bg-[#C5A059]/10 hover:bg-[#C5A059]/20 border border-[#C5A059]/40 hover:border-[#C5A059] transition"
                        >
                          <span className="flex items-center gap-2 text-[11px] sm:text-xs font-accent text-[#0A192F]">
                            <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
                            OR GET ALL 5 FOR {bundle.symbol}{bundle.price.toFixed(0)}
                          </span>
                          <span className="text-[10px] font-accent text-[#C5A059] group-hover:text-[#0A192F]">
                            SAVE {Math.round(((bundle.original_price - bundle.price) / bundle.original_price) * 100)}%
                            <ArrowRight className="inline w-3 h-3 ml-1 -mt-0.5" />
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
